/**
 * Keeps the cart-page "Multi-project savings" message
 * (snippets/multi-project-shipping-promo.liquid) in step with the cart after
 * AJAX additions, removals and quantity changes.
 *
 * Several scripts mutate the cart directly (cart.js, nested-cart-items.js,
 * cart-checkout-property-transform.js), so rather than hook each one this
 * watches fetch() for the cart-mutation endpoints and recomputes from
 * /cart.js. Dawn's cartUpdate pub/sub event is used too,
 * when it carries a full cart.
 *
 * countProjects() must stay in sync with the snippet and with the discount
 * Function (multi-project-shipping-discount/src/run.ts).
 */
(function () {
  'use strict';

  if (customElements.get('multi-project-shipping-promo')) return;

  var MIN_DISTINCT_PROJECTS = 2;
  var ADDITIONAL_SPREADS_SKU_SUFFIX = 'ADDPAGE';
  var ADDITIONAL_SPREADS_TITLE_RE = /^additional (spreads|pages)\b/i;
  var CART_MUTATION_PATTERN = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var REFRESH_DEBOUNCE_MS = 150;

  var originalFetch = window.fetch;

  function isAdditionalSpreadsLine(item) {
    var sku = typeof item.sku === 'string' ? item.sku : '';
    if (sku.slice(-ADDITIONAL_SPREADS_SKU_SUFFIX.length) === ADDITIONAL_SPREADS_SKU_SUFFIX) return true;
    // The theme's add-on rule (cart-visible-count.liquid, cart-checkout-
    // property-transform.js). Stricter than the Function, so it can only
    // under-promise, never claim a discount the Function won't give.
    var skuLower = sku.toLowerCase();
    if (skuLower.indexOf('-addspread') !== -1 || skuLower.indexOf('-addpage') !== -1) return true;
    return ADDITIONAL_SPREADS_TITLE_RE.test(String(item.product_title || '').trim());
  }

  function projectIdForLine(item) {
    var props = item.properties || {};
    var value = props._projectId != null ? props._projectId : props.projectId;
    return value != null && value !== '' ? String(value) : null;
  }

  function countProjects(items) {
    var seen = {};
    var count = 0;
    items.forEach(function (item) {
      if (isAdditionalSpreadsLine(item)) return;
      var projectId = projectIdForLine(item);
      if (!projectId || seen[projectId]) return;
      seen[projectId] = true;
      count += 1;
    });
    return count;
  }

  function stateForCart(cart) {
    var items = (cart && cart.items) || [];
    if (!items.length) return 'hidden';
    var count = countProjects(items);
    if (count >= MIN_DISTINCT_PROJECTS) return 'qualified';
    if (count === 1) return 'promo';
    // Items but no project id: eligibility can't be shown reliably.
    return 'hidden';
  }

  function isFullCart(data) {
    return Boolean(data && Array.isArray(data.items) && typeof data.item_count === 'number');
  }

  class MultiProjectShippingPromo extends HTMLElement {
    connectedCallback() {
      this.requestSequence = 0;
      this.refreshTimer = null;
      this.onMutation = this.scheduleRefresh.bind(this);
      this.onPageShow = (event) => {
        if (event.persisted) this.scheduleRefresh();
      };

      document.addEventListener('multi-project-promo:cart-mutated', this.onMutation);
      window.addEventListener('pageshow', this.onPageShow);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && isFullCart(event.cartData)) {
            this.requestSequence += 1;
            this.render(stateForCart(event.cartData));
          } else {
            this.scheduleRefresh();
          }
        });
      }

      // Scripts that ran before this one (e.g. property normalization on load)
      // may already have changed the cart the server rendered.
      this.scheduleRefresh();
    }

    disconnectedCallback() {
      document.removeEventListener('multi-project-promo:cart-mutated', this.onMutation);
      window.removeEventListener('pageshow', this.onPageShow);
      if (this.unsubscribe) this.unsubscribe();
      clearTimeout(this.refreshTimer);
    }

    scheduleRefresh() {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), REFRESH_DEBOUNCE_MS);
    }

    refresh() {
      var sequence = (this.requestSequence += 1);
      var url = ((window.routes && window.routes.cart_url) || '/cart') + '.js';

      originalFetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('cart.js ' + response.status);
          return response.json();
        })
        .then((cart) => {
          if (sequence === this.requestSequence) this.render(stateForCart(cart));
        })
        .catch(() => {
          // Unknown cart state: don't leave an unverified message on screen.
          if (sequence === this.requestSequence) this.render('hidden');
        });
    }

    render(state) {
      if (this.dataset.state === state) return;
      this.dataset.state = state;
      this.hidden = state === 'hidden';
      this.querySelectorAll('[data-promo-state]').forEach(function (message) {
        message.hidden = message.dataset.promoState !== state;
      });
    }
  }

  customElements.define('multi-project-shipping-promo', MultiProjectShippingPromo);

  if (!window.__multiProjectPromoFetchWrapped && typeof originalFetch === 'function') {
    window.__multiProjectPromoFetchWrapped = true;
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || String(input || '');
      var promise = originalFetch.apply(this, arguments);
      if (CART_MUTATION_PATTERN.test(url)) {
        var notify = function () {
          document.dispatchEvent(new CustomEvent('multi-project-promo:cart-mutated'));
        };
        promise.then(notify, notify);
      }
      return promise;
    };
  }
})();
